import React, { useCallback, useEffect, useRef, useState } from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import Switch from "@mui/material/Switch"
import Typography from "@mui/material/Typography"
import Snackbar from "@mui/material/Snackbar"
import Alert from "@mui/material/Alert"
import CloseIcon from "@mui/icons-material/Close"
import MinimizeIcon from "@mui/icons-material/Remove"
import MaximizeIcon from "@mui/icons-material/CropSquare"
import TerminalIcon from "@mui/icons-material/Terminal"
import AddIcon from "@mui/icons-material/Add"
import SettingsIcon from "@mui/icons-material/Settings"
import ReplayIcon from "@mui/icons-material/Replay"
import { loadSshSettings, useTerminal, getSecondaries, getProjectDirs, tierLabel, checkTmuxSessionAlive } from "./TerminalContext"
import { useDeployEvents } from "../deploy/DeployEventContext"
import DeployProjectDialog from "../deploy/DeployProjectDialog"
import TerminalSession from "./TerminalSession"
import { ProfileTabDialog, NewTabDialog } from "./TerminalDialogs"
import { DESKTOP_LAYERS } from "../desktop/desktopLayers"

// ── Constants ──────────────────────────────────────────────────────────────────

const MIN_W = 320
const MIN_H = 200
const TITLE_H = 44
const DEFAULT_W = 880
const DEFAULT_H = 520
const MARGIN = 12
const NAV = 64
const SNAP_DIST = 32
const EDGE = 18      // fallback hit-zone for getEdgeFromEvent
const HANDLE_W = 28  // overlay resize handle width (wider = easier to grab)

const STATUS_COLOR = {
  idle: "var(--app-text-dim)",
  connecting: "var(--app-warning)",
  connected: "var(--app-success)",
  error: "var(--app-error)",
}

const ATTENTION_COLOR = "var(--app-warning)"

// ── Snap helpers ───────────────────────────────────────────────────────────────

function snapZoneAt(mx, my) {
  const ww = window.innerWidth
  const wh = window.innerHeight
  const nearLeft  = mx < NAV + SNAP_DIST
  const nearRight = mx > ww - SNAP_DIST
  const nearTop   = my < SNAP_DIST
  const nearBot   = my > wh - SNAP_DIST

  if (nearLeft && nearTop)    return "tl"
  if (nearRight && nearTop)   return "tr"
  if (nearLeft && nearBot)    return "bl"
  if (nearRight && nearBot)   return "br"
  if (nearLeft)               return "l"
  if (nearRight)              return "r"
  if (nearTop)                return "full"
  return null
}

function snapRect(zone) {
  const ww = window.innerWidth
  const wh = window.innerHeight
  const half  = Math.floor((ww - NAV) / 2)
  const halfH = Math.floor(wh / 2)
  switch (zone) {
    case "tl":   return { x: NAV + MARGIN, y: MARGIN, w: half - MARGIN, h: halfH - MARGIN }
    case "tr":   return { x: NAV + half, y: MARGIN, w: ww - NAV - half - MARGIN, h: halfH - MARGIN }
    case "bl":   return { x: NAV + MARGIN, y: halfH, w: half - MARGIN, h: halfH - MARGIN }
    case "br":   return { x: NAV + half, y: halfH, w: ww - NAV - half - MARGIN, h: halfH - MARGIN }
    case "l":    return { x: NAV, y: 0, w: half, h: wh }
    case "r":    return { x: NAV + half, y: 0, w: ww - NAV - half, h: wh }
    case "full": return { x: NAV, y: 0, w: ww - NAV, h: wh }
    default:     return null
  }
}

// Border-radius + edge glow for the pinned sides
function snapBorderStyle(zone) {
  if (!zone) return {}
  const glow = "color-mix(in srgb, var(--app-accent) 55%, transparent)"
  const pinT = ["tl", "tr", "full"].includes(zone)
  const pinB = ["bl", "br"].includes(zone)
  const pinL = ["tl", "bl", "l", "full"].includes(zone)
  const pinR = ["tr", "br", "r", "full"].includes(zone)
  const tl = (pinT && pinL) ? 0 : 6
  const tr = (pinT && pinR) ? 0 : 6
  const br = (pinB && pinR) ? 0 : 6
  const bl = (pinB && pinL) ? 0 : 6
  return {
    borderRadius: `${tl}px ${tr}px ${br}px ${bl}px`,
    ...(pinT && { borderTop:    `2px solid ${glow}` }),
    ...(pinB && { borderBottom: `2px solid ${glow}` }),
    ...(pinL && { borderLeft:   `2px solid ${glow}` }),
    ...(pinR && { borderRight:  `2px solid ${glow}` }),
  }
}

// ── Resize cursor per edge/corner ──────────────────────────────────────────────

function resizeCursor(edge) {
  const map = {
    n: "n-resize", s: "s-resize",
    e: "e-resize", w: "w-resize",
    ne: "ne-resize", nw: "nw-resize",
    se: "se-resize", sw: "sw-resize",
  }
  return map[edge] ?? "default"
}

function getEdgeFromEvent(e, el) {
  const r = el.getBoundingClientRect()
  const x = e.clientX - r.left
  const y = e.clientY - r.top
  const w = r.width
  const h = r.height
  const onN = y < EDGE
  const onS = y > h - EDGE
  const onW = x < EDGE
  const onE = x > w - EDGE

  if (onN && onW) return "nw"
  if (onN && onE) return "ne"
  if (onS && onW) return "sw"
  if (onS && onE) return "se"
  if (onN) return "n"
  if (onS) return "s"
  if (onW) return "w"
  if (onE) return "e"
  return null
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function TerminalWindow() {
  const {
    open, minimized, closeTerminal, toggleMinimize, mode, pinned,
    sessions, activeId, setActiveId, ensureSessions,
    addShellSession, addAgentSession, closeSession, reconnectSession,
    updateStatus, updateAttention, updateSessionId,
  } = useTerminal()
  const docked = mode === "docked"

  const geomRef = useRef({ x: 88, y: 72, w: DEFAULT_W, h: DEFAULT_H })
  const [geom, setGeomState] = useState({ ...geomRef.current })
  const [snap,  setSnap]  = useState(null)
  const [ghost, setGhost] = useState(null)
  const [isMoving, setIsMoving] = useState(false)

  // Split-screen: when set, this session id is shown side-by-side with activeId.
  const [splitId, setSplitId] = useState(null)
  const [splitRatio, setSplitRatio] = useState(0.5)   // left-pane fraction (0.2–0.8)
  const [dragOverSplit, setDragOverSplit] = useState(false)
  const { deployState, requestDeploy, autoAccept, setAutoAccept } = useDeployEvents()
  const [deployError, setDeployError] = useState(null)
  const [deployBlocked, setDeployBlocked] = useState(null)
  const [projectDialogOpen, setProjectDialogOpen] = useState(false)
  const deploying = deployState.status === "building" || deployState.status === "awaiting_confirm"
  const [profileDialogOpen, setProfileDialogOpen] = useState(false)
  const [newTabDialogOpen, setNewTabDialogOpen] = useState(false)
  const [secondaries, setSecondaries] = useState(() => getSecondaries())
  const hasSecondary = secondaries.length > 0
  const [projectDirs, setProjectDirs] = useState(() => getProjectDirs())


  // Refresh secondaries + projectDirs when SSH settings change
  useEffect(() => {
    const handler = () => {
      setSecondaries(getSecondaries())
      setProjectDirs(getProjectDirs())
    }
    window.addEventListener("ssh-settings-changed", handler)
    return () => window.removeEventListener("ssh-settings-changed", handler)
  }, [])

  const winRef        = useRef(null)
  const dockedRef     = useRef(docked)
  useEffect(() => { dockedRef.current = docked }, [docked])
  const dragRef       = useRef({ on: false, sx: 0, sy: 0, ox: 0, oy: 0 })
  const resizeRef     = useRef({ on: false, edge: null, sx: 0, sy: 0, ox: 0, oy: 0, ow: 0, oh: 0 })
  const fitCallbacks  = useRef({})
  const focusCallbacks = useRef({})
  const rafRef        = useRef(null)

  const registerFit   = useCallback((id) => (fn) => { fitCallbacks.current[id] = fn }, [])
  const registerFocus = useCallback((id) => (fn) => { focusCallbacks.current[id] = fn }, [])

  // Restore (or reattach to) the shared tab list when the floating window opens.
  // The list itself lives in TerminalContext so the IDE side panel shares it and
  // it survives navigation; here we only ensure it is populated and reset the
  // floating-only split + xterm callback maps when the window is closed.
  useEffect(() => {
    if (open && !pinned) ensureSessions()
    if (!open) {
      setSplitId(null)
      fitCallbacks.current = {}
      focusCallbacks.current = {}
    }
  }, [open, pinned, ensureSessions])

  // Refit and focus active session when restoring from minimize
  useEffect(() => {
    if (!minimized && activeId) {
      setTimeout(() => fitCallbacks.current[activeId]?.(), 100)
      setTimeout(() => focusCallbacks.current[activeId]?.(), 120)
    }
  }, [minimized, activeId])

  const addShellTab = useCallback((profileOverride, projectDir) => {
    addShellSession(profileOverride, projectDir)
    setNewTabDialogOpen(false)
  }, [addShellSession])

  const addTab = useCallback(() => {
    const multiDirs = getProjectDirs().length > 1
    if (hasSecondary || multiDirs) {
      setNewTabDialogOpen(true)
    } else {
      addShellTab(null, null)
    }
  }, [hasSecondary, addShellTab])

  const addTabWithProfile = useCallback((profileName, username, password, projectDir) => {
    addAgentSession(profileName, username, password, projectDir)
    setProfileDialogOpen(false)
  }, [addAgentSession])

  const reconnect = useCallback(() => {
    if (activeId != null) reconnectSession(activeId)
  }, [activeId, reconnectSession])

  const closeTab = useCallback((id, e) => {
    e?.stopPropagation()
    delete fitCallbacks.current[id]
    delete focusCallbacks.current[id]
    // Closing either member of a split returns to single-pane view; never let the
    // new active tab (the last remaining one) equal the split partner. Compute the
    // fallback from the current snapshot — closeSession applies the removal itself.
    const remaining = sessions.filter((s) => s.id !== id)
    const fallback = remaining.length ? remaining[remaining.length - 1].id : null
    setSplitId((sp) => (sp === id || sp === fallback ? null : sp))
    closeSession(id)
  }, [sessions, closeSession])

  // Reconnect a disconnected ("idle"/grey) tab. Its tmux session may have died on
  // the host in the meantime (process exited, host rebooted, someone killed it by
  // hand) — reconnecting blindly would silently spawn a brand-new session under
  // the same tab. Check first: if the tmux session is truly gone, drop the tab
  // with an explanation instead of resurrecting it under a fresh identity.
  const [tabToast, setTabToast] = useState(null)
  const reconnectTab = useCallback(async (id, e) => {
    e?.stopPropagation()
    const s = sessions.find((x) => x.id === id)
    if (!s) return
    if (!s.sessionId) { reconnectSession(id); return }

    const cfg = loadSshSettings()
    const username = s.profileOverride?.username ?? cfg.username
    const password = s.profileOverride?.password ?? cfg.password
    if (!username || !cfg.host) { reconnectSession(id); return }

    const alive = await checkTmuxSessionAlive({
      host: cfg.host, port: cfg.port, username, password, sessionId: s.sessionId,
    })

    if (alive === false) {
      setTabToast(`"${s.label ?? "tab"}" was already killed on the host — tab removed`)
      closeTab(id)
    } else {
      // alive === true, or null (check itself failed) → attempt a normal reconnect
      reconnectSession(id)
    }
  }, [sessions, reconnectSession, closeTab])

  // ── Split-screen (drag a tab onto the terminal body) ──────────────────────────
  const contentRef = useRef(null)
  const dividerOn  = useRef(false)
  const dragTabId  = useRef(null)

  const fitSplit = useCallback(() => {
    setTimeout(() => {
      fitCallbacks.current[activeId]?.()
      if (splitId != null) fitCallbacks.current[splitId]?.()
    }, 60)
  }, [activeId, splitId])

  // Refit both panes whenever the split is entered/left or the ratio settles.
  useEffect(() => { fitSplit() }, [splitId, fitSplit])

  const onTabDragStart = useCallback((id, e) => {
    dragTabId.current = id
    e.dataTransfer.effectAllowed = "move"
    try { e.dataTransfer.setData("text/plain", String(id)) } catch {}
  }, [])

  const onContentDragOver = useCallback((e) => {
    if (dragTabId.current == null) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    if (dragTabId.current !== activeId) setDragOverSplit(true)
  }, [activeId])

  const onContentDrop = useCallback((e) => {
    e.preventDefault()
    setDragOverSplit(false)
    const dropped = dragTabId.current
    dragTabId.current = null
    if (dropped == null || dropped === activeId) return   // can't split a session with itself
    setSplitId(dropped)
  }, [activeId])

  const onDividerDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    dividerOn.current = true
  }, [])

  const onDividerMove = useCallback((e) => {
    if (!dividerOn.current || !contentRef.current) return
    const r = contentRef.current.getBoundingClientRect()
    const frac = Math.max(0.2, Math.min(0.8, (e.clientX - r.left) / r.width))
    setSplitRatio(frac)
  }, [])

  const onDividerUp = useCallback((e) => {
    if (!dividerOn.current) return
    dividerOn.current = false
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch {}
    fitSplit()
  }, [fitSplit])

  // ── Drag — DOM-direct, zero React renders during gesture ───────────────────────
  const onTitlePointerDown = useCallback((e) => {
    if (e.target.closest("button")) return
    if (e.button !== 0) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)

    const el = winRef.current
    let ox = geomRef.current.x
    let oy = geomRef.current.y

    if (snap) {
      if (el) { const r = el.getBoundingClientRect(); ox = r.left; oy = r.top }
      setSnap(null)
      if (el) { el.style.left = ox + "px"; el.style.top = oy + "px"; el.style.right = ""; el.style.bottom = "" }
      geomRef.current = { ...geomRef.current, x: ox, y: oy }
    }

    dragRef.current = { on: true, sx: e.clientX, sy: e.clientY, ox, oy }
    setIsMoving(true)
  }, [snap])

  const onTitlePointerMove = useCallback((e) => {
    if (!dragRef.current.on) return
    const { sx, sy, ox, oy } = dragRef.current
    const nx = Math.max(0, Math.min(window.innerWidth - geomRef.current.w, ox + e.clientX - sx))
    const ny = Math.max(0, Math.min(window.innerHeight - TITLE_H, oy + e.clientY - sy))

    const el = winRef.current
    if (el) { el.style.left = nx + "px"; el.style.top = ny + "px" }

    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      const zone = snapZoneAt(e.clientX, e.clientY)
      setGhost(zone ? snapRect(zone) : null)
    })
  }, [])

  const onTitlePointerUp = useCallback((e) => {
    if (!dragRef.current.on) return
    dragRef.current.on = false
    e.currentTarget.releasePointerCapture(e.pointerId)
    setIsMoving(false)
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null }

    const zone = snapZoneAt(e.clientX, e.clientY)
    if (zone) {
      const r = snapRect(zone)
      if (winRef.current) winRef.current.style.cssText = ""
      geomRef.current = { x: r.x, y: r.y, w: r.w, h: r.h }
      setGeomState({ ...geomRef.current })
      setSnap(zone)
      setTimeout(() => fitCallbacks.current[activeId]?.(), 80)
    } else {
      const el = winRef.current
      if (el) {
        const r = el.getBoundingClientRect()
        geomRef.current = { ...geomRef.current, x: r.left, y: r.top }
        el.style.cssText = ""
        setGeomState({ ...geomRef.current })
      }
    }
    setGhost(null)
  }, [activeId])

  // ── Border resize — DOM-direct ─────────────────────────────────────────────────
  const [resizeCursorStyle, setResizeCursorStyle] = useState("default")

  const onWinPointerMove = useCallback((e) => {
    if (resizeRef.current.on) {
      const { edge, sx, sy, ox, oy, ow, oh } = resizeRef.current
      const dx = e.clientX - sx
      const dy = e.clientY - sy
      const el = winRef.current

      if (dockedRef.current) {
        // Docked: "n" only changes height (bottom stays anchored), "e" only changes width
        if (edge === "n" && el) el.style.height = Math.max(MIN_H, oh - dy) + "px"
        else if (edge === "e" && el) el.style.width = Math.max(MIN_W, ow + dx) + "px"
      } else {
        let x = ox, y = oy, w = ow, h = oh
        if (edge.includes("e")) w = Math.max(MIN_W, ow + dx)
        if (edge.includes("s")) h = Math.max(MIN_H, oh + dy)
        if (edge.includes("w")) { const nw = Math.max(MIN_W, ow - dx); x = ox + ow - nw; w = nw }
        if (edge.includes("n")) { const nh = Math.max(MIN_H, oh - dy); y = oy + oh - nh; h = nh }
        if (el) {
          el.style.left = x + "px"; el.style.top = y + "px"
          el.style.width = w + "px"; el.style.height = h + "px"
          el.style.right = ""; el.style.bottom = ""
        }
      }
      return
    }

    // Cursor detection only applies in floating mode
    if (!dragRef.current.on && winRef.current && !minimized && !dockedRef.current) {
      const edge = getEdgeFromEvent(e, winRef.current)
      setResizeCursorStyle(edge ? resizeCursor(edge) : "default")
    }
  }, [minimized])

  const onWinPointerDown = useCallback((e) => {
    if (!winRef.current) return
    if (minimized) return  // no resize when minimized — only drag title bar
    if (docked) return     // docked mode uses explicit overlay handles only
    const edge = getEdgeFromEvent(e, winRef.current)
    if (!edge) return
    if (e.target.closest("[data-title-bar]")) return

    e.preventDefault()
    e.stopPropagation()
    winRef.current.setPointerCapture(e.pointerId)

    let g = geomRef.current
    if (snap) {
      const r = winRef.current.getBoundingClientRect()
      g = { x: r.left, y: r.top, w: r.width, h: r.height }
      geomRef.current = g
      setSnap(null)
    }

    resizeRef.current = { on: true, edge, sx: e.clientX, sy: e.clientY, ox: g.x, oy: g.y, ow: g.w, oh: g.h }
    setIsMoving(true)
  }, [snap, minimized])

  const onWinPointerUp = useCallback((e) => {
    if (!resizeRef.current.on) return
    resizeRef.current.on = false
    try { winRef.current?.releasePointerCapture(e.pointerId) } catch {}
    setIsMoving(false)
    setResizeCursorStyle("default")

    const el = winRef.current
    if (el) {
      const r = el.getBoundingClientRect()
      if (dockedRef.current) {
        // Docked: position (x/y) is controlled by CSS (bottom:0/left:NAV); only sync w/h
        geomRef.current = { ...geomRef.current, w: r.width, h: r.height }
      } else {
        geomRef.current = { x: r.left, y: r.top, w: r.width, h: r.height }
      }
      // Clear residual inline styles so React's sx controls position/size again.
      // Without this, el.style.height from the resize gesture overrides the CSS
      // class on the next minimize or snap, breaking both features.
      el.style.cssText = ""
      setGeomState({ ...geomRef.current })
    }
    if (activeId && fitCallbacks.current[activeId]) {
      setTimeout(() => fitCallbacks.current[activeId]?.(), 50)
    }
  }, [activeId])

  // Overlay resize handles call this — captures pointer to winRef so onWinPointerMove/Up fire
  const startResizeFromHandle = useCallback((e, edge) => {
    e.preventDefault()
    e.stopPropagation()
    if (!winRef.current || minimized) return
    winRef.current.setPointerCapture(e.pointerId)
    // Always read actual DOM rect — geomRef.current.y is wrong in docked/snapped modes
    // because position is controlled by bottom:0 or snap CSS, not by geomRef coords.
    const r = winRef.current.getBoundingClientRect()
    const g = { x: r.left, y: r.top, w: r.width, h: r.height }
    geomRef.current = g
    if (snap) setSnap(null)
    resizeRef.current = { on: true, edge, sx: e.clientX, sy: e.clientY, ox: g.x, oy: g.y, ow: g.w, oh: g.h }
    setIsMoving(true)
  }, [snap, minimized])

  // ── Compute window position style ───────────────────────────────────────────
  const wStyle = (() => {
    const h = minimized ? TITLE_H : geom.h
    if (docked) return { left: NAV, bottom: 0, width: geom.w, height: h }
    if (!snap) return { left: geom.x, top: geom.y, width: geom.w, height: h }
    switch (snap) {
      case "tl":   return { left: NAV + MARGIN, top: MARGIN, width: geom.w, height: h }
      case "tr":   return { right: MARGIN, top: MARGIN, width: geom.w, height: h }
      case "bl":   return { left: NAV + MARGIN, bottom: MARGIN, width: geom.w, height: h }
      case "br":   return { right: MARGIN, bottom: MARGIN, width: geom.w, height: h }
      case "l":    return { left: NAV, top: 0, width: geom.w, height: minimized ? TITLE_H : "100vh" }
      case "r":    return { right: 0, top: 0, width: geom.w, height: minimized ? TITLE_H : "100vh" }
      case "full": return { left: NAV, top: 0, right: 0, height: minimized ? TITLE_H : "100vh" }
      default:     return { left: geom.x, top: geom.y, width: geom.w, height: h }
    }
  })()

  if (!open || pinned) return null

  const activeSession = sessions.find((s) => s.id === activeId)
  const stColor = STATUS_COLOR[activeSession?.status] ?? "var(--app-text-dim)"

  const primaryUsername = loadSshSettings().username
  const profileChoices = [
    { name: "primary", label: "Primary", username: primaryUsername },
    ...secondaries.map((username) => ({ name: username, label: tierLabel(username), username })),
  ]

  return (
    <>
      {/* ── New tab dialog (profile + dir selection) ───────────────────────── */}
      <NewTabDialog
        open={newTabDialogOpen}
        onClose={() => setNewTabDialogOpen(false)}
        secondaries={secondaries}
        projectDirs={projectDirs}
        onOpen={addShellTab}
      />

      {/* ── Deploy project selector ─────────────────────────────────────────── */}
      <DeployProjectDialog
        open={projectDialogOpen}
        onClose={() => setProjectDialogOpen(false)}
        onSelect={async (project, metadata) => {
          setProjectDialogOpen(false)
          try {
            const sshCreds = loadSshSettings()
            const data = await requestDeploy(sshCreds, project, metadata)
            if (!data.ok) {
              if (data.blocked) setDeployBlocked(data.message)
              else setDeployError(data.error ?? "Deploy failed")
            }
          } catch (err) {
            setDeployError(err.message)
          }
        }}
      />

      {/* ── Profile switch dialog (gear icon — opens agent tab) ────────────── */}
      <ProfileTabDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        profileChoices={profileChoices}
        projectDirs={projectDirs}
        onOpen={(chosen, dir) => {
          const cfg = loadSshSettings()
          addTabWithProfile(chosen.label, chosen.username, cfg.password, dir)
        }}
      />

      {/* ── Ghost snap preview (floating only) ─────────────────────────────── */}
      {!docked && ghost && (
        <Box
          sx={{
            position: "fixed",
            zIndex: DESKTOP_LAYERS.floatingUtility - 1,
            left: ghost.x,
            top: ghost.y,
            width: ghost.w,
            height: ghost.h,
            background: "color-mix(in srgb, var(--app-accent) 10%, transparent)",
            border: "2px dashed color-mix(in srgb, var(--app-accent) 55%, transparent)",
            borderRadius: 2,
            pointerEvents: "none",
            backdropFilter: "blur(2px)",
          }}
        />
      )}

      {/* ── Terminal window ─────────────────────────────────────────────────── */}
      <Box
        ref={winRef}
        onPointerMove={onWinPointerMove}
        onPointerDown={onWinPointerDown}
        onPointerUp={onWinPointerUp}
        sx={{
          position: "fixed",
          zIndex: DESKTOP_LAYERS.floatingUtility,
          ...wStyle,
          ...(!docked ? snapBorderStyle(snap) : {}),
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: docked
            ? "1px solid color-mix(in srgb, var(--app-accent) 14%, transparent)"
            : snap ? undefined : "1px solid rgba(var(--app-fg-rgb),0.09)",
          borderBottom: docked ? "none" : undefined,
          borderLeft: docked ? "none" : undefined,
          boxShadow: docked
            ? "4px -4px 36px rgba(var(--app-shadow-rgb),0.65)"
            : "0 24px 64px rgba(var(--app-shadow-rgb),0.75), 0 0 0 1px rgba(var(--app-fg-rgb),0.04)",
          backgroundColor: "var(--app-bg)",
          cursor: resizeCursorStyle,
          transition: isMoving ? "none" : "box-shadow 0.2s, border-radius 0.15s",
          userSelect: "none",
          "& .xterm-screen": { pointerEvents: isMoving ? "none" : "auto" },
        }}
      >
        {/* ── Title bar ──────────────────────────────────────────────────────── */}
        <Box
          data-title-bar
          onPointerDown={docked ? undefined : onTitlePointerDown}
          onPointerMove={docked ? undefined : onTitlePointerMove}
          onPointerUp={docked ? undefined : onTitlePointerUp}
          sx={{
            height: TITLE_H,
            px: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "var(--app-bg-alt)",
            borderBottom: "1px solid rgba(var(--app-fg-rgb),0.07)",
            cursor: docked ? "default" : (isMoving ? "grabbing" : "grab"),
            flexShrink: 0,
            touchAction: "none",
          }}
        >
          <TerminalIcon sx={{ fontSize: 16, color: stColor, flexShrink: 0 }} />
          <Typography
            sx={{
              fontFamily: "var(--app-font-mono)",
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              color: "rgba(var(--app-fg-rgb),0.65)",
              flexShrink: 0,
            }}
          >
            SSH
          </Typography>

          {/* Tab bar — always visible (even when minimized) */}
          <Box sx={{
            display: "flex", gap: 0.5, ml: 1, overflow: "hidden", flex: 1, alignItems: "center",
            "@keyframes tab-attention": {
              "0%, 100%": { backgroundColor: "rgba(249,115,22,0.10)", borderColor: "rgba(249,115,22,0.30)" },
              "50%":       { backgroundColor: "rgba(249,115,22,0.28)", borderColor: "rgba(249,115,22,0.65)" },
            },
          }}>
            {sessions.map((s, idx) => (
              <Box
                key={s.id}
                draggable
                onDragStart={(e) => onTabDragStart(s.id, e)}
                onDragEnd={() => { dragTabId.current = null; setDragOverSplit(false) }}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => {
                  // Clicking the split partner just focuses it; otherwise it becomes
                  // the (left) active pane — never let active === split.
                  if (s.id !== splitId) setActiveId(s.id)
                  setTimeout(() => focusCallbacks.current[s.id]?.(), 60)
                }}
                sx={{
                  px: 1, py: 0.25,
                  display: "flex", alignItems: "center", gap: 0.5,
                  borderRadius: 1,
                  cursor: "grab",
                  flexShrink: 0,
                  backgroundColor: s.attention
                    ? "rgba(249,115,22,0.10)"
                    : (s.id === activeId || s.id === splitId) ? "rgba(var(--app-fg-rgb),0.09)" : "transparent",
                  border: "1px solid",
                  borderColor: s.attention
                    ? "rgba(249,115,22,0.30)"
                    : s.id === splitId ? "color-mix(in srgb, var(--app-accent) 50%, transparent)"
                    : s.id === activeId ? "rgba(var(--app-fg-rgb),0.14)" : "transparent",
                  animation: s.attention ? "tab-attention 1.4s ease-in-out infinite" : "none",
                  "&:hover": {
                    backgroundColor: s.attention
                      ? "rgba(249,115,22,0.22)"
                      : "rgba(var(--app-fg-rgb),0.06)",
                  },
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: s.attention ? ATTENTION_COLOR : STATUS_COLOR[s.status], flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.65rem", color: s.id === activeId ? "#fff" : "rgba(var(--app-fg-rgb),0.4)", fontFamily: "var(--app-font-mono)", whiteSpace: "nowrap" }}>
                  {s.label ?? (idx === 0 ? loadSshSettings().agent || "agent" : `shell ${idx}`)}
                </Typography>
                {s.status === "idle" && (
                  <Tooltip title="Reconnect" placement="top">
                    <IconButton
                      size="small"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => reconnectTab(s.id, e)}
                      sx={{ p: 0, ml: 0.25, color: "var(--app-success)", "&:hover": { color: "#4ade80" } }}
                    >
                      <ReplayIcon sx={{ fontSize: 10 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  size="small"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => closeTab(s.id, e)}
                  sx={{ p: 0, ml: 0.25, color: "rgba(239,68,68,0.65)", "&:hover": { color: "var(--app-error)" } }}
                >
                  <CloseIcon sx={{ fontSize: 9 }} />
                </IconButton>
              </Box>
            ))}
            {!minimized && (
              <IconButton
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={addTab}
                sx={{ p: 0.4, color: "rgba(var(--app-fg-rgb),0.3)", "&:hover": { color: "rgba(var(--app-fg-rgb),0.7)" }, flexShrink: 0 }}
              >
                <AddIcon sx={{ fontSize: 13 }} />
              </IconButton>
            )}
          </Box>

          {/* Auto-accept toggle — auto-confirm incoming deploy requests from this tab */}
          <Tooltip title={autoAccept ? "Auto-accept on — this tab confirms deploys automatically" : "Auto-accept off — confirm deploys manually"} placement="top">
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Typography sx={{ fontSize: "0.55rem", fontFamily: "var(--app-font-mono)", letterSpacing: "0.06em", color: autoAccept ? "var(--app-success)" : "rgba(var(--app-fg-rgb),0.4)" }}>
                auto
              </Typography>
              <Switch
                size="small"
                checked={autoAccept}
                onChange={(e) => setAutoAccept(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--app-success)" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "var(--app-success)" },
                }}
              />
            </Box>
          </Tooltip>

          {/* Deploy button */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }} onPointerDown={(e) => e.stopPropagation()}>
            <Tooltip
              title={
                deployState.status === "building" ? "Deploy in progress…" :
                deployState.status === "awaiting_confirm" ? "Waiting for all tabs to confirm…" :
                "Start deploy — all open tabs must confirm"
              }
              placement="top"
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={deploying}
                  onClick={() => {
                    setDeployError(null)
                    setDeployBlocked(null)
                    setProjectDialogOpen(true)
                  }}
                  sx={{
                    height: 22, px: 1, py: 0, minWidth: 0,
                    fontSize: "0.6rem",
                    fontFamily: "var(--app-font-mono)",
                    letterSpacing: "0.06em",
                    color: deploying ? "var(--app-warning)" : "var(--app-success)",
                    borderColor: deploying ? "rgba(234,179,8,0.4)" : "rgba(34,197,94,0.4)",
                    flexShrink: 0, lineHeight: 1, textTransform: "none",
                    "&:hover": {
                      borderColor: deploying ? "rgba(234,179,8,0.7)" : "rgba(34,197,94,0.7)",
                      backgroundColor: deploying ? "rgba(234,179,8,0.08)" : "rgba(34,197,94,0.08)",
                    },
                    "&.Mui-disabled": { color: "rgba(234,179,8,0.5)", borderColor: "rgba(234,179,8,0.25)" },
                  }}
                >
                  {deployState.status === "building" ? "deploying…" : deployState.status === "awaiting_confirm" ? "awaiting…" : "deploy"}
                </Button>
              </span>
            </Tooltip>
            {(deployError || deployBlocked) && (
              <Typography
                sx={{
                  fontSize: "0.55rem",
                  fontFamily: "var(--app-font-mono)",
                  color: deployBlocked ? "var(--app-warning)" : "var(--app-error)",
                  maxWidth: 160,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.2,
                  mt: 0.25,
                  cursor: "default",
                }}
                title={deployError ?? deployBlocked}
              >
                {deployError ?? deployBlocked}
              </Typography>
            )}
          </Box>

          {/* Profile switcher — only visible when secondary account exists */}
          {hasSecondary && (
            <Tooltip title="SSH-Profil wechseln" placement="top">
              <IconButton
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => setProfileDialogOpen(true)}
                sx={{ color: "color-mix(in srgb, var(--feature-vault) 70%, transparent)", p: 0.5, flexShrink: 0, "&:hover": { color: "var(--feature-vault)" } }}
              >
                <SettingsIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Reconnect button */}
          {activeSession && (activeSession.status === "idle" || activeSession.status === "error") && (
            <Tooltip title="Connect" placement="top">
              <IconButton
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={reconnect}
                sx={{ color: "var(--app-success)", p: 0.5, flexShrink: 0, "&:hover": { color: "#4ade80" } }}
              >
                <TerminalIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={minimized ? "Restore" : "Minimize"} placement="top">
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={toggleMinimize}
              sx={{ color: "rgba(var(--app-fg-rgb),0.28)", p: 0.5, flexShrink: 0, "&:hover": { color: "rgba(var(--app-fg-rgb),0.7)" } }}
            >
              {minimized ? <MaximizeIcon sx={{ fontSize: 14 }} /> : <MinimizeIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Close" placement="top">
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={closeTerminal}
              sx={{ color: "rgba(var(--app-fg-rgb),0.28)", p: 0.5, flexShrink: 0, "&:hover": { color: "var(--app-error)" } }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Terminal area — always rendered so SSH sessions survive minimize ── */}
        <Box
          ref={contentRef}
          onPointerDown={(e) => {
            // In split view each xterm handles its own focus; forcing the active
            // pane here would steal focus from the other pane.
            if (splitId == null && !resizeRef.current.on && !e.target.closest("button")) {
              focusCallbacks.current[activeId]?.()
            }
          }}
          onDragOver={onContentDragOver}
          onDragLeave={() => setDragOverSplit(false)}
          onDrop={onContentDrop}
          sx={{
            flex: 1,
            overflow: "hidden",
            display: minimized ? "none" : "flex",
            flexDirection: splitId != null ? "row" : "column",
            position: "relative",
            backgroundColor: "var(--app-bg)",
            cursor: "text",
            ...(dragOverSplit && {
              outline: "2px dashed color-mix(in srgb, var(--app-accent) 55%, transparent)",
              outlineOffset: "-4px",
            }),
          }}
        >
          {sessions.map((s) => {
            const inSplit = splitId != null
            const isLeft  = s.id === activeId
            const isRight = inSplit && s.id === splitId
            const active  = inSplit ? (isLeft || isRight) : isLeft
            const paneStyle = (inSplit && (isLeft || isRight))
              ? {
                  flexGrow: 0, flexShrink: 0,
                  flexBasis: `calc(${(isLeft ? splitRatio : 1 - splitRatio) * 100}% - 3px)`,
                  order: isLeft ? 0 : 2,
                }
              : undefined
            return (
              <TerminalSession
                key={s.id}
                id={s.id}
                active={active}
                paneStyle={paneStyle}
                minimized={minimized}
                reconnectToggle={s.reconnectToggle}
                startupCommand={s.startupCommand}
                sessionId={s.sessionId}
                profileOverride={s.profileOverride}
                projectDirOverride={s.projectDirOverride}
                onStatusChange={(status, msg) => updateStatus(s.id, status, msg)}
                onAttentionChange={(needs) => updateAttention(s.id, needs)}
                onFitReady={registerFit(s.id)}
                onFocusReady={registerFocus(s.id)}
                onSessionId={(sid) => updateSessionId(s.id, sid)}
              />
            )
          })}

          {/* Draggable divider between the two split panes */}
          {splitId != null && (
            <Box
              onPointerDown={onDividerDown}
              onPointerMove={onDividerMove}
              onPointerUp={onDividerUp}
              sx={{
                order: 1, flex: "0 0 6px", alignSelf: "stretch",
                cursor: "col-resize", backgroundColor: "color-mix(in srgb, var(--app-accent) 18%, transparent)",
                "&:hover": { backgroundColor: "color-mix(in srgb, var(--app-accent) 45%, transparent)" },
                zIndex: 4,
              }}
            />
          )}

          {/* Exit-split affordance */}
          {splitId != null && (
            <Tooltip title="Split beenden">
              <IconButton
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => setSplitId(null)}
                sx={{
                  position: "absolute", top: 4, right: 6, zIndex: 7, p: 0.3,
                  color: "rgba(var(--app-fg-rgb),0.45)", backgroundColor: "rgba(8,8,15,0.7)",
                  "&:hover": { color: "var(--app-accent)", backgroundColor: "rgba(8,8,15,0.9)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* ── Overlay resize handles — sit above xterm, reliable cursor + capture ── */}
        {!minimized && (docked ? [
          // Docked mode: top edge (height) + right edge (width) only.
          // "n" is intentionally thin (6px) — title bar is immediately below and must stay clickable.
          { k: "n", s: { top: 0, left: 0, right: 0, height: 6, cursor: "n-resize" } },
          { k: "e", s: { right: 0, top: TITLE_H, bottom: 0, width: HANDLE_W, cursor: "e-resize" } },
        ] : [
          // Floating mode: all edges
          { k: "w",  s: { left: 0,  top: TITLE_H, bottom: 0,       width: HANDLE_W,  cursor: "w-resize" } },
          { k: "e",  s: { right: 0, top: TITLE_H, bottom: 0,       width: HANDLE_W,  cursor: "e-resize" } },
          { k: "s",  s: { bottom: 0, left: HANDLE_W, right: HANDLE_W, height: HANDLE_W, cursor: "s-resize" } },
          { k: "sw", s: { bottom: 0, left: 0,  width: HANDLE_W, height: HANDLE_W, cursor: "sw-resize" } },
          { k: "se", s: { bottom: 0, right: 0, width: HANDLE_W, height: HANDLE_W, cursor: "se-resize" } },
        ]).map(({ k, s }) => (
          <Box
            key={k}
            sx={{ position: "absolute", zIndex: 5, ...s }}
            onPointerDown={(e) => startResizeFromHandle(e, k)}
          />
        ))}

        {/* ── Decorative SE corner grip ───────────────────────────────────────── */}
        {!minimized && (
          <Box
            sx={{
              position: "absolute",
              bottom: 2, right: 2,
              width: 12, height: 12,
              pointerEvents: "none",
              zIndex: 6,
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 2, right: 2,
                width: 7, height: 7,
                borderRight: "2px solid rgba(var(--app-fg-rgb),0.15)",
                borderBottom: "2px solid rgba(var(--app-fg-rgb),0.15)",
                borderRadius: "0 0 2px 0",
              },
            }}
          />
        )}
      </Box>

      <Snackbar
        open={!!tabToast}
        autoHideDuration={5000}
        onClose={() => setTabToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setTabToast(null)}
          sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.72rem" }}
        >
          {tabToast}
        </Alert>
      </Snackbar>
    </>
  )
}
